import { shallowRef } from "vue";
import type { TourDefinition } from "@/tours/types";

// Session state outlives runs: the refs are what the UI reads, and the
// counters let async work from an older start or step see that it is stale.
// Everything a run changes is in `run`, which begin and resume reset with what
// they carry in, and stop (or a page tour's page changing) resets when it ends.
export const activeTourKey = shallowRef<string | null>(null);
// Waiting for the registry: popups held for a tour must not slip out first.
export const startingTour = shallowRef(false);
// Counted so the host can explain a page tour that couldn't load.
export const failedLoads = shallowRef(0);

interface Session {
  // A start, or a resume, overtaken while the registry loads (by a later
  // start or the user changing page) is dropped.
  runRequest: number;
  // Bumped on every step change so a slow anchor wait can tell it is stale.
  stepToken: number;
  // Not the user leaving. An object, so an older navigation finishing can't
  // clear a newer one's; tagged with the step that started it, so only the
  // live step's own landing counts.
  tourNavigation: { path: string; stepToken: number } | null;
}

export const session: Session = {
  runRequest: 0,
  stepToken: 0,
  tourNavigation: null,
};

export interface TourPosition {
  tour: TourDefinition;
  index: number;
}

export interface TourRun {
  // Set before the step appears, so leaving the page ends a tour still waiting.
  currentStep: TourPosition | null;
  activeStep: TourPosition | null;
  // Tells a page tour that found nothing from one the user finished, and a
  // tour the user saw from one that never got as far as showing.
  stepsShown: number;
  // The server takes no write for an ended tour but a restart at step 0.
  restarted: boolean;
  // A resume the user didn't ask for fails without telling them.
  resumed: boolean;
  // onArrival runs once per run, not on every visit to step 1, which can be
  // looked for again before it shows.
  arrived: boolean;
  undoArrival: (() => void) | null;
  // Shared: another instance (the router hook) can end the tour.
  trackedTour: TourDefinition | null;
  // driver.js only hands focus back after drive(); this engine uses highlight().
  returnFocus: HTMLElement | null;
  lastTarget: Element | null;
  // A tour the user ended by clicking into the page leaves focus where they put it.
  pointedInPage: boolean;
  // Back returns to the step shown before, not to one that was skipped.
  shownSteps: number[];
  // So re-finding a step's control doesn't save it again.
  savedStep: string;
  // A step's action may open another page. A deadline, so it can't swallow a
  // later navigation of the user's own.
  actionNavigationUntil: number;
  // The app may open what was saved after the next step is already showing.
  actionNavigationAfterSave: boolean;
  // Their controls going away isn't the user closing them.
  submitsPending: number;
  // Counts clicks on a step's own Save; a submit's wait that sees one after it
  // started leaves the save to that click.
  ownSaveClicks: number;
}

function newRun(): TourRun {
  return {
    currentStep: null,
    activeStep: null,
    stepsShown: 0,
    restarted: false,
    resumed: false,
    arrived: false,
    undoArrival: null,
    trackedTour: null,
    returnFocus: null,
    lastTarget: null,
    pointedInPage: false,
    shownSteps: [],
    savedStep: "",
    actionNavigationUntil: 0,
    actionNavigationAfterSave: false,
    submitsPending: 0,
    ownSaveClicks: 0,
  };
}

export const run: TourRun = newRun();

export function resetRun(carry: Partial<TourRun> = {}) {
  Object.assign(run, newRun(), carry);
}

const ACTION_NAVIGATION_MS = 3000;

export function allowActionNavigation(afterSave = false) {
  run.actionNavigationUntil = Date.now() + ACTION_NAVIGATION_MS;
  run.actionNavigationAfterSave = afterSave;
}
